import { useCallback } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";

import {
  CreateStageDocument,
  type CreateStageInput,
  DeleteStageDocument,
  ReorderStageDocument,
  SetDefaultStageDocument,
  UpdateStageDocument,
  type UpdateStageInput,
  ValidateStageDeletionDocument,
} from "#app/lib/data";
import { removeFromConnection } from "#app/lib/data/connection-cache-utils";
import { useAnalytics, AnalyticsContext, SettingsEvent } from "#app/lib/analytics";

export function useStageMutations() {
  const client = useApolloClient();
  const { track } = useAnalytics();

  // No cache insert on create: `stages` is cached per workflow (and under a
  // defaultStage isDefault-filtered alias), so an unscoped
  // insertIntoConnection would land the new stage in OTHER workflows' cached
  // lists. The active settings list refetches instead.
  const [createStageMutation] = useMutation(CreateStageDocument, {
    refetchQueries: ["StagesByWorkflow"],
  });

  const [updateStageMutation] = useMutation(UpdateStageDocument);
  const [reorderStageMutation] = useMutation(ReorderStageDocument);

  const [deleteStageMutation] = useMutation(DeleteStageDocument, {
    update(cache, { data }) {
      const result = data?.deleteStage;
      if (result?.__typename === "DeleteStageSuccess") {
        removeFromConnection(
          cache,
          "stages",
          { __typename: "Stage", stageId: result.stage.stageId },
          { evict: true },
        );
      }
    },
  });

  const [setDefaultStageMutation] = useMutation(SetDefaultStageDocument);

  const createStage = useCallback(
    async (input: CreateStageInput) => {
      const { data } = await createStageMutation({
        variables: { input },
      });
      track({ context: AnalyticsContext.Settings, event: SettingsEvent.Create, label: "stage" });
      return data?.createStage ?? null;
    },
    [createStageMutation, track],
  );

  const updateStage = useCallback(
    async (stageId: string, input: UpdateStageInput) => {
      const { data } = await updateStageMutation({
        variables: { id: stageId, input },
      });
      track({ context: AnalyticsContext.Settings, event: SettingsEvent.Update, label: "stage" });
      return data?.updateStage ?? null;
    },
    [updateStageMutation, track],
  );

  const reorderStage = useCallback(
    async (stageId: string, sortOrder: number) => {
      const { data } = await reorderStageMutation({
        variables: { id: stageId, sortOrder },
      });
      track({ context: AnalyticsContext.Settings, event: SettingsEvent.Reorder, label: "stage" });
      return data?.reorderStage ?? null;
    },
    [reorderStageMutation, track],
  );

  const deleteStage = useCallback(
    async (stageId: string) => {
      const { data } = await deleteStageMutation({
        variables: { id: stageId },
      });
      track({ context: AnalyticsContext.Settings, event: SettingsEvent.Delete, label: "stage" });
      return data?.deleteStage ?? null;
    },
    [deleteStageMutation, track],
  );

  const setDefaultStage = useCallback(
    async (workflowId: string, stageId: string) => {
      const { data } = await setDefaultStageMutation({
        variables: { workflowId, stageId },
      });
      track({ context: AnalyticsContext.Settings, event: SettingsEvent.SetDefault, label: "stage" });
      return data?.setDefaultStage ?? null;
    },
    [setDefaultStageMutation, track],
  );

  const validateStageDeletion = useCallback(
    async (stageId: string) => {
      const { data, error } = await client.query({
        query: ValidateStageDeletionDocument,
        variables: { id: stageId },
        fetchPolicy: "network-only",
      });
      // errorPolicy "all" resolves with `error` set instead of rejecting —
      // rethrow so the caller's catch path stays the failure path.
      if (!data) throw error ?? new Error("Failed to validate stage deletion");
      return data.validateStageDeletion;
    },
    [client],
  );

  return {
    createStage,
    updateStage,
    reorderStage,
    deleteStage,
    setDefaultStage,
    validateStageDeletion,
  };
}
